import { ChevronDown, ChevronRight, cn, Icon } from "@dust-tt/sparkle";
import type React from "react";
import { useState } from "react";

const collapsedRowCount = 3;

interface DetailsSectionProps<T> {
  title: string;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  emptyState: React.ReactNode;
  count?: number;
  defaultOpen?: boolean;
  footer?: React.ReactNode;
  onShowAll?: () => void;
  layout?: "list" | "chips";
}

export function DetailsSection<T>({
  title,
  items,
  renderItem,
  emptyState,
  count = items.length,
  defaultOpen = true,
  footer,
  onShowAll,
  layout = "list",
}: DetailsSectionProps<T>) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);

  const hiddenCount = items.length - collapsedRowCount;
  const visibleItems =
    showAll || layout === "chips" ? items : items.slice(0, collapsedRowCount);

  return (
    <section className="flex flex-col py-2">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((value) => !value)}
        className="-mx-2 flex items-center gap-1.5 rounded-lg px-2 py-2 text-left hover:bg-muted-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-sm font-semibold text-foreground">{title}</span>
        {count > 0 && (
          <span className="text-sm text-muted-foreground">{count}</span>
        )}
        <Icon
          visual={ChevronDown}
          size="xs"
          className={cn(
            "ml-auto text-muted-foreground transition-transform duration-200",
            !isOpen && "-rotate-90"
          )}
        />
      </button>
      <DetailsCollapse open={isOpen}>
        <div className="flex flex-col pt-1">
          {items.length === 0 ? (
            emptyState
          ) : (
            <ul
              className={
                layout === "chips"
                  ? "flex flex-wrap gap-2 py-1"
                  : "flex flex-col gap-0.5"
              }
            >
              {visibleItems.map((item, index) => (
                <li key={index} className="min-w-0">
                  {renderItem(item)}
                </li>
              ))}
            </ul>
          )}
          {layout === "list" && hiddenCount > 0 && (
            <button
              type="button"
              onClick={onShowAll ?? (() => setShowAll((value) => !value))}
              className="mt-1 flex w-fit items-center gap-1 rounded-md px-0.5 text-sm font-medium text-highlight hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {showAll ? "Show less" : `Show ${hiddenCount} more`}
              <Icon
                visual={ChevronDown}
                size="xs"
                className={cn(showAll && "rotate-180")}
              />
            </button>
          )}
          {footer}
        </div>
      </DetailsCollapse>
    </section>
  );
}

interface DetailsCollapseProps {
  open: boolean;
  children: React.ReactNode;
}

// Animates height through grid rows (0fr ↔ 1fr), so content of any height slides without
// measuring; `invisible` lands after the transition and drops the hidden rows from tab order.
export function DetailsCollapse({ open, children }: DetailsCollapseProps) {
  return (
    <div
      aria-hidden={!open}
      className={cn(
        "grid transition-[grid-template-rows,opacity,visibility] duration-200 ease-out motion-reduce:transition-none",
        open
          ? "visible grid-rows-[1fr] opacity-100"
          : "invisible grid-rows-[0fr] opacity-0"
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

interface DetailsRowProps {
  visual: React.ReactNode;
  tileClassName?: string;
  title: string;
  subtitle?: React.ReactNode;
  muted?: boolean;
  onClick?: () => void;
}

export function DetailsRow({
  visual,
  tileClassName,
  title,
  subtitle,
  muted,
  onClick,
}: DetailsRowProps) {
  const content = (
    <>
      <DetailsIconTile className={tileClassName}>{visual}</DetailsIconTile>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            "truncate text-sm font-medium",
            muted ? "text-muted-foreground" : "text-foreground"
          )}
        >
          {title}
        </span>
        {subtitle && (
          <span className="truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
        )}
      </span>
      {onClick && (
        <Icon
          visual={ChevronRight}
          size="xs"
          className="shrink-0 text-muted-foreground opacity-60 transition-opacity group-hover/row:opacity-100"
        />
      )}
    </>
  );

  const className = "-mx-2 flex w-full items-center gap-3 rounded-lg px-2 py-2";

  if (!onClick) {
    return <div className={className}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        className,
        "group/row text-left hover:bg-muted-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      )}
    >
      {content}
    </button>
  );
}

interface DetailsIconTileProps {
  children: React.ReactNode;
  className?: string;
}

export function DetailsIconTile({ children, className }: DetailsIconTileProps) {
  return (
    <span
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground",
        className && cn("border-transparent", className)
      )}
    >
      {children}
    </span>
  );
}

interface DetailsChipProps {
  visual: React.ReactNode;
  label: string;
  muted?: boolean;
  onClick: () => void;
}

export function DetailsChip({
  visual,
  label,
  muted,
  onClick,
}: DetailsChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={cn(
        "flex max-w-full items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm hover:bg-muted-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        muted ? "border-dashed text-muted-foreground" : "text-foreground"
      )}
    >
      <span className="flex shrink-0 items-center">{visual}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}

interface DetailsEmptyStateProps {
  visual: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

export function DetailsEmptyState({
  visual,
  title,
  description,
}: DetailsEmptyStateProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-border px-3 py-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted-background text-muted-foreground">
        <Icon visual={visual} size="sm" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-foreground">{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </div>
  );
}
